import { useEffect, useMemo, useState } from "react";
import { Button, showError } from "@common";
import api from "@api/api";
import endpoints from "@api/endpoints";
import { useAssignedLabInvoiceDownloads } from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { formatMoney } from "@utils/format";
import { torontoDateParts } from "@utils";

function pad(value) {
  return String(value).padStart(2, "0");
}

function defaultRange() {
  const now = torontoDateParts();
  const month = `${now.year}-${pad(now.month)}`;
  return { from: `${month}-01`, to: `${month}-${pad(now.day)}` };
}

const ALL_PRACTITIONERS = "__all__";

export default function LabInvoiceDownloads() {
  const initial = useMemo(defaultRange, []);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [selectedClinic, setSelectedClinic] = useState("");
  const [selectedPractitioner, setSelectedPractitioner] = useState(ALL_PRACTITIONERS);
  const [downloading, setDownloading] = useState(false);

  const filters = useMemo(() => ({ date_from: dateFrom, date_to: dateTo }), [dateFrom, dateTo]);
  const { data, isLoading, isError, error } = useAssignedLabInvoiceDownloads(filters);
  const clinics = useMemo(() => data?.clinics || [], [data?.clinics]);

  // Keep the clinic selection valid as the date range / data changes.
  useEffect(() => {
    if (clinics.length === 0) {
      if (selectedClinic !== "") setSelectedClinic("");
      return;
    }
    if (!clinics.some((clinic) => clinic.clinic === selectedClinic)) {
      setSelectedClinic(clinics[0].clinic);
      setSelectedPractitioner(ALL_PRACTITIONERS);
    }
  }, [clinics, selectedClinic]);

  const clinic = useMemo(
    () => clinics.find((item) => item.clinic === selectedClinic) || null,
    [clinics, selectedClinic],
  );
  const practitioners = useMemo(() => clinic?.practitioners || [], [clinic]);
  const practitioner = useMemo(
    () =>
      selectedPractitioner === ALL_PRACTITIONERS
        ? null
        : practitioners.find((item) => item.practitioner_id === selectedPractitioner) || null,
    [practitioners, selectedPractitioner],
  );

  const visibleAttachments = useMemo(() => {
    if (!clinic) return [];
    if (practitioner) return practitioner.attachments || [];
    return practitioners.flatMap((item) => item.attachments || []);
  }, [clinic, practitioner, practitioners]);

  async function downloadZip() {
    if (!clinic) return;
    setDownloading(true);
    try {
      const params = { clinic: clinic.clinic, date_from: dateFrom, date_to: dateTo };
      if (practitioner) params.practitioner = practitioner.practitioner_id;
      const response = await api.get(endpoints.LAB_INVOICE_DOWNLOAD_ZIP, {
        params,
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      const labelParts = [clinic.label, practitioner?.label, dateFrom, dateTo].filter(Boolean);
      link.href = blobUrl;
      link.download = `${labelParts.join("_")}.zip`.replace(/[^A-Za-z0-9._-]+/g, "_");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to download ZIP."));
    } finally {
      setDownloading(false);
    }
  }

  async function downloadOne(attachmentId) {
    try {
      const response = await api.get(endpoints.GET_LAB_INVOICE_ATTACHMENT_VIEW_URL(attachmentId));
      if (response.data?.url) window.open(response.data.url, "_blank", "noopener");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to open PDF."));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-md border border-palette-slate-divider bg-palette-slate-soft px-3 py-2">
        <label className="text-xs font-medium text-palette-slate-badge">
          From
          <input
            type="date"
            className="mt-1 block rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
          />
        </label>
        <label className="text-xs font-medium text-palette-slate-badge">
          To
          <input
            type="date"
            className="mt-1 block rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
          />
        </label>
        <label className="text-xs font-medium text-palette-slate-badge">
          Clinic
          <select
            className="mt-1 block w-56 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
            value={selectedClinic}
            disabled={clinics.length === 0}
            onChange={(event) => {
              setSelectedClinic(event.target.value);
              setSelectedPractitioner(ALL_PRACTITIONERS);
            }}
          >
            {clinics.length === 0 && <option value="">No clinics</option>}
            {clinics.map((item) => (
              <option key={item.clinic} value={item.clinic}>
                {item.label} ({item.count})
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium text-palette-slate-badge">
          Practitioner
          <select
            className="mt-1 block w-56 rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
            value={selectedPractitioner}
            disabled={!clinic}
            onChange={(event) => setSelectedPractitioner(event.target.value)}
          >
            <option value={ALL_PRACTITIONERS}>
              All practitioners{clinic ? ` (${clinic.count})` : ""}
            </option>
            {practitioners.map((item) => (
              <option key={item.practitioner_id} value={item.practitioner_id}>
                {item.label} ({item.count})
              </option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={!clinic || visibleAttachments.length === 0 || downloading}
          onClick={downloadZip}
        >
          {downloading ? "Preparing…" : "Download ZIP"}
        </Button>
        {isLoading && <span className="text-xs text-palette-slate-secondary">Loading…</span>}
      </div>

      {isError && (
        <div className="text-sm text-palette-rose-badge">
          {getApiErrorMessage(error, "Unable to load assigned lab invoices.")}
        </div>
      )}

      {!isLoading && !isError && clinics.length === 0 && (
        <div className="text-sm text-palette-slate-secondary">
          No assigned lab invoices in this date range.
        </div>
      )}

      {clinic && (
        <div className="rounded-md border border-palette-slate-divider bg-white">
          <div className="border-b border-palette-slate-muted px-3 py-2 text-xs text-palette-slate-secondary">
            {visibleAttachments.length} PDF{visibleAttachments.length === 1 ? "" : "s"}
            {" · "}
            {practitioner ? practitioner.label : "All practitioners"} · {clinic.label}
          </div>
          <ul>
            {visibleAttachments.map((attachment) => (
              <li
                key={attachment.id}
                className="flex flex-wrap items-center justify-between gap-2 border-t border-palette-slate-soft px-3 py-1.5 text-xs first:border-t-0"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="text-palette-slate-placeholder">📄</span>
                  <span className="truncate text-palette-slate-heading">
                    {attachment.original_filename}
                  </span>
                  <span className="whitespace-nowrap text-palette-slate-placeholder">
                    {attachment.assigned_date || ""} · {formatMoney(attachment.amount)}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!attachment.has_pdf}
                  onClick={() => downloadOne(attachment.id)}
                >
                  {attachment.has_pdf ? "Download" : "No PDF"}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
